import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Skeleton } from './ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plane, MapPin, Plus, Crown, TrendingUp, Compass,
  ArrowRight, Clock, Heart, Users, Zap, ChevronRight
} from 'lucide-react';
import { motion } from 'framer-motion';
import ChallengeWidget from './ChallengeWidget';
import QuizWidget from './QuizWidget';

import { API } from '../config';

const POST_TYPE_STYLES = {
  gate_change: { label: 'Gate Change', cls: 'chip-gate' },
  crowd_level: { label: 'Crowd Alert', cls: 'chip-crowd' },
  food_rec: { label: 'Food Tip', cls: 'chip-food' },
  delay_alert: { label: 'Delay Alert', cls: 'chip-delay' },
  lounge_tip: { label: 'Lounge Tip', cls: 'chip-lounge' },
  general: { label: 'Update', cls: 'chip-crowd' },
  experience: { label: 'Experience', cls: 'chip-lounge' },
  seat_tip: { label: 'Seat Tip', cls: 'chip-food' },
  recommendation: { label: 'Tip', cls: 'chip-crowd' },
};

export default function LoggedInHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [myFlights, setMyFlights] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [trendingFlights, setTrendingFlights] = useState([]);
  const [airports, setAirports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [flightNumber, setFlightNumber] = useState('');
  const [flightDate, setFlightDate] = useState('');
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [myFlightsRes, discoverRes, airRes] = await Promise.all([
          axios.get(`${API}/users/me/flights`, { withCredentials: true }).catch(() => ({ data: [] })),
          axios.get(`${API}/discover`).catch(() => ({ data: {} })),
          axios.get(`${API}/airports`).catch(() => ({ data: [] })),
        ]);
        setMyFlights(myFlightsRes.data?.slice(0, 5) || []);
        setRecentPosts(discoverRes.data?.recent_posts?.slice(0, 6) || []);
        setTrendingFlights(discoverRes.data?.trending_flights?.slice(0, 4) || []);
        setAirports(airRes.data?.slice(0, 6) || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  const handleCheckin = async () => {
    if (!flightNumber.trim()) { toast.error('Enter a flight number'); return; }
    setCheckingIn(true);
    try {
      const res = await axios.post(
        `${API}/flights/checkin`,
        { flight_number: flightNumber, flight_date: flightDate || undefined },
        { withCredentials: true }
      );
      toast.success(`Flight ${flightNumber.toUpperCase()} logged to your records!`);
      setCheckinOpen(false);
      setFlightNumber('');
      setFlightDate('');
      // Navigate to the route community
      const routeId = res.data.route_id;
      navigate(`/route/${routeId}`);
    } catch {
      toast.error('Check-in failed. Try again.');
    } finally {
      setCheckingIn(false);
    }
  };

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(iso).toLocaleDateString();
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      {/* Personalized header */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>
              Welcome back, <span className="text-[#FF4500]">{user?.name?.split(' ')[0]}</span> ✈️
            </h1>
            <p className="text-muted-foreground mt-1">
              {user?.flight_count || 0} flights logged • {user?.badges?.length || 0} badges earned
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Dialog open={checkinOpen} onOpenChange={setCheckinOpen}>
              <DialogTrigger asChild>
                <Button className="fly-btn-primary" data-testid="hero-checkin-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Log a Flight
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle style={{ fontFamily: 'Space Grotesk' }}>Log Your Flight</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2" data-testid="flight-checkin-form">
                  <p className="text-sm text-muted-foreground">Log your flight to your travel history and join the route community.</p>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Flight Number *</label>
                    <Input
                      placeholder="e.g. EK101, SV001, QR567"
                      value={flightNumber}
                      onChange={e => setFlightNumber(e.target.value.toUpperCase())}
                      onKeyDown={e => e.key === 'Enter' && handleCheckin()}
                      className="text-base uppercase"
                      data-testid="flight-number-input"
                      autoFocus
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">Flight Date</label>
                    <Input
                      type="date"
                      value={flightDate}
                      onChange={e => setFlightDate(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2 pt-1">
                    {['EK101', 'SV001', 'QR567', 'TK234', 'BA089'].map(fn => (
                      <button key={fn} onClick={() => setFlightNumber(fn)}
                        className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-[#FF4500]/10 hover:text-[#FF4500] transition-colors">{fn}</button>
                    ))}
                  </div>
                  <Button onClick={handleCheckin} disabled={checkingIn} className="fly-btn-primary w-full" data-testid="flight-checkin-submit-button">
                    {checkingIn ? 'Logging flight...' : 'Log Flight & Join Route Community'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            {!user?.is_premium && (
              <Button variant="outline" size="sm" asChild>
                <Link to="/premium"><Crown className="w-3.5 h-3.5 mr-1 text-[#FF4500]" /> Upgrade</Link>
              </Button>
            )}
          </div>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-6">
          {[
            { label: 'Flights Logged', value: user?.flight_count || 0, icon: Plane, color: '#FF4500' },
            { label: 'Badges', value: user?.badges?.length || 0, icon: Zap, color: '#1E3A8A' },
            { label: 'Followers', value: user?.followers?.length || 0, icon: Users, color: '#10B981' },
            { label: 'Following', value: user?.following?.length || 0, icon: Users, color: '#10B981', hideOnMobile: true },
          ].map(({ label, value, icon: Icon, color, hideOnMobile }) => (
            <Card key={label} className={`fly-card p-3 sm:p-4 ${hideOnMobile ? 'hidden sm:block' : ''}`}>
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4" style={{ color }} />
                <span className="text-xs text-muted-foreground">{label}</span>
              </div>
              <p className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Space Grotesk', color }}>{value}</p>
            </Card>
          ))}
        </div>
      </motion.div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">

          {/* My Recent Flights */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                <Plane className="w-4 h-4 text-[#FF4500]" /> My Recent Flights
              </h2>
              <Link to={`/profile/${user?.user_id}`} className="text-xs text-[#FF4500] hover:underline flex items-center gap-1">
                View all <ChevronRight className="w-3 h-3" />
              </Link>
            </div>

            {loading ? (
              <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-16 rounded-xl" />)}</div>
            ) : myFlights.length === 0 ? (
              <Card className="fly-card p-6 text-center">
                <Plane className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground mb-3">No flights logged yet</p>
                <Button size="sm" className="fly-btn-primary" onClick={() => setCheckinOpen(true)}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Log Your First Flight
                </Button>
              </Card>
            ) : (
              <div className="space-y-2">
                {myFlights.map((record, i) => (
                  <motion.div key={record.record_id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}>
                    <Card
                      className="fly-card p-3 hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => navigate(`/route/${record.origin_code}-${record.destination_code}`)}
                      data-testid="my-flights-flight-card"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-[#FF4500]/10 flex items-center justify-center flex-shrink-0">
                            <Plane className="w-4 h-4 text-[#FF4500]" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-bold text-sm" style={{ fontFamily: 'Space Grotesk' }}>{record.flight_number}</span>
                              <span className="text-sm text-muted-foreground flex items-center gap-1">
                                {record.origin_code} <Plane className="w-2.5 h-2.5" /> {record.destination_code}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              {formatDate(record.flight_date)}
                              {record.seat && <span className="ml-1">• Seat {record.seat}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-[#FF4500]">
                          View route <ArrowRight className="w-3 h-3" />
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </section>

          {/* Live Community Feed */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
                <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                Live Community Feed
              </h2>
              <Link to="/discover" className="text-xs text-[#FF4500] hover:underline flex items-center gap-1">
                Explore <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
            {loading ? (
              <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
            ) : (
              <div className="space-y-3">
                {recentPosts.map((post, i) => {
                  const ts = POST_TYPE_STYLES[post.post_type] || POST_TYPE_STYLES.general;
                  return (
                    <motion.div key={post.post_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.05 }}>
                      <Card className="fly-card p-4" data-testid="feed-post-card">
                        <div className="flex gap-3">
                          <img src={post.author_avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${post.author_id}`} alt="" className="w-8 h-8 rounded-full flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="text-sm font-medium">{post.author_name}</span>
                              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${ts.cls}`}>{ts.label}</span>
                              {post.airport_code && (
                                <span className="flex items-center gap-0.5 text-xs text-muted-foreground ml-auto">
                                  <MapPin className="w-3 h-3" />{post.airport_code}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-foreground leading-relaxed line-clamp-2">{post.content}</p>
                            <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{(post.reactions?.like || 0) + (post.reactions?.love || 0)}</span>
                              <span>{timeAgo(post.created_at)}</span>
                            </div>
                          </div>
                        </div>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </section>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* User profile card */}
          <Card className="fly-card p-4">
            <div className="flex items-center gap-3 mb-3">
              <Avatar className="w-12 h-12">
                <AvatarImage src={user?.avatar} />
                <AvatarFallback className="bg-[#FF4500] text-white font-bold">{user?.name?.[0]}</AvatarFallback>
              </Avatar>
              <div>
                <div className="font-semibold text-sm">{user?.name}</div>
                <div className="text-xs text-muted-foreground">{user?.email}</div>
              </div>
            </div>
            {user?.badges?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {user.badges.slice(0, 3).map(b => (
                  <Badge key={b} className="text-[10px] bg-[#1E3A8A]/15 text-[#1E3A8A] border border-[#1E3A8A]/20">{b}</Badge>
                ))}
              </div>
            )}
            <Button size="sm" variant="outline" className="w-full" asChild>
              <Link to={`/profile/${user?.user_id}`}>View My Profile</Link>
            </Button>
          </Card>

          {/* Weekly Challenges */}
          <ChallengeWidget />

          {/* Daily Quiz */}
          <QuizWidget />

          {/* Trending Flights */}
          {trendingFlights.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5" style={{ fontFamily: 'Space Grotesk' }}>
                <TrendingUp className="w-4 h-4 text-[#FF4500]" /> Trending Routes
              </h3>
              <div className="space-y-2">
                {trendingFlights.map(f => (
                  <Card
                    key={f.flight_id}
                    className="fly-card p-3 cursor-pointer hover:shadow-md transition-shadow"
                    onClick={() => navigate(`/route/${f.route_id || `${f.origin_code}-${f.destination_code}`}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-sm font-bold" style={{ fontFamily: 'Space Grotesk' }}>{f.flight_number}</div>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          {f.origin_code} <Plane className="w-2.5 h-2.5" /> {f.destination_code}
                        </div>
                      </div>
                      <Badge className={`text-[10px] ${f.status === 'on_time' ? 'status-on-time' : f.status === 'boarding' ? 'status-boarding' : 'status-delayed'}`}>
                        {f.status?.replace('_', ' ')}
                      </Badge>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Quick Links */}
          <Card className="fly-card p-4">
            <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Space Grotesk' }}>Explore</h3>
            <div className="space-y-1">
              {[
                ['/discover', 'Discover Airports', Compass],
                ['/airlines', 'Airlines Directory', Plane],
                ['/buddy', 'Find Layover Buddy', Users],
                ['/premium', 'Fly N Out Club', Crown],
              ].map(([href, label, Icon]) => (
                <Link key={href} to={href} className="flex items-center gap-2 p-2 rounded-lg hover:bg-muted transition-colors text-sm">
                  <Icon className="w-4 h-4 text-muted-foreground" />
                  {label}
                </Link>
              ))}
            </div>
          </Card>

          {/* Featured Airports */}
          <div>
            <h3 className="text-sm font-semibold mb-2" style={{ fontFamily: 'Space Grotesk' }}>Top Airports</h3>
            <div className="grid grid-cols-3 gap-2">
              {airports.slice(0, 6).map(apt => (
                <Link key={apt.iata_code} to={`/airport/${apt.iata_code}`}>
                  <Card className="fly-card p-2 text-center hover:shadow-md hover:-translate-y-0.5 transition-all">
                    <div className="w-8 h-8 mx-auto mb-1 rounded-lg overflow-hidden">
                      <img src={apt.image_url} alt={apt.city} className="w-full h-full object-cover" onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                    <div className="text-xs font-bold text-[#FF4500]">{apt.iata_code}</div>
                    <div className="text-[10px] text-muted-foreground truncate">{apt.city}</div>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
